/**
 * Employee ID series — company-configurable employee numbers (prefix, zero-padding, next number),
 * one series per legal entity (Keka parity, task EMP-5).
 *
 * When an ENABLED series exists for an employee's legal entity, `employees.create` allocates the
 * code from it (atomically, inside the create transaction). With no enabled series the code is the
 * random one it always was. Codes already issued are never rewritten.
 *
 * Configuration, so it reuses `org.settings:read` / `org.settings:update` (no new permission), like
 * the grade and cost-centre catalogues.
 *
 * Setting `nextNumber` is unrestricted: a value that would produce a code already in use is saved,
 * and the PUT response carries a `warning`. The allocator skips over taken codes at issue time, so
 * a collision can never produce a duplicate employee code.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const EMPLOYEE_NUMBER_PREFIX_MAX = 10;
export const EMPLOYEE_NUMBER_PADDING_MAX = 10;
export const EMPLOYEE_NUMBER_NEXT_MAX = 999_999_999;

/**
 * prefix + the number left-padded with zeros to `padding` digits. A number wider than `padding`
 * is never truncated (padding 3, number 1234 -> "1234"). Pure, and shared so the web preview is
 * exactly what the API will issue.
 */
export function formatEmployeeCode(prefix: string, padding: number, value: number): string {
  return `${prefix}${String(value).padStart(padding, '0')}`;
}

export const employeeNumberPrefixSchema = z
  .string()
  .max(EMPLOYEE_NUMBER_PREFIX_MAX)
  .regex(/^[A-Z0-9_-]*$/, 'Use uppercase letters, digits, "-" or "_" only');

export const employeeNumberSeriesSchema = z.object({
  legalEntityId: uuidSchema,
  legalEntityCode: z.string(),
  name: z.string(),
  /** False for an entity that has never had a series saved; the other fields are the defaults. */
  isConfigured: z.boolean(),
  prefix: employeeNumberPrefixSchema,
  padding: z.number().int().min(0).max(EMPLOYEE_NUMBER_PADDING_MAX),
  nextNumber: z.number().int().min(1),
  isEnabled: z.boolean(),
  /** The code the next new employee would get, e.g. "EMP0042". */
  preview: z.string(),
});
export type EmployeeNumberSeries = z.infer<typeof employeeNumberSeriesSchema>;

export const upsertEmployeeNumberSeriesBodySchema = z.object({
  prefix: employeeNumberPrefixSchema,
  padding: z.number().int().min(0).max(EMPLOYEE_NUMBER_PADDING_MAX),
  nextNumber: z.number().int().min(1).max(EMPLOYEE_NUMBER_NEXT_MAX),
  isEnabled: z.boolean(),
});
export type UpsertEmployeeNumberSeriesBody = z.infer<typeof upsertEmployeeNumberSeriesBodySchema>;

export const upsertEmployeeNumberSeriesResultSchema = employeeNumberSeriesSchema.extend({
  /** Set when the saved next code already belongs to an employee; issuing will skip past it. */
  warning: z.string().nullable(),
});
export type UpsertEmployeeNumberSeriesResult = z.infer<
  typeof upsertEmployeeNumberSeriesResultSchema
>;

const readMeta = routeMeta('org.settings:read');
const writeMeta = routeMeta('org.settings:update');

export const employeeNumberSeriesContract = c.router({
  listEmployeeNumberSeries: {
    method: 'GET',
    path: '/employee-number-series',
    responses: {
      200: z.object({ items: z.array(employeeNumberSeriesSchema) }),
      401: errorBodySchema,
      403: errorBodySchema,
    },
    summary: 'List the employee ID series of every legal entity, including ones not set up yet',
    metadata: readMeta,
  },
  upsertEmployeeNumberSeries: {
    method: 'PUT',
    path: '/employee-number-series/:legalEntityId',
    pathParams: z.object({ legalEntityId: uuidSchema }),
    body: upsertEmployeeNumberSeriesBodySchema,
    responses: {
      200: upsertEmployeeNumberSeriesResultSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      422: errorBodySchema,
    },
    summary: "Create or replace a legal entity's employee ID series",
    metadata: writeMeta,
  },
});
